import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import type { PortalReturnSummary } from "../../domain/portal";
import { listReturns, getReturnDetails, cancelReturn } from "../../services/api/portal";
import { readPortalSession } from "../../lib/storage/portalSession";
import { normalizeError } from "../../services/http/errors";

const statusColor = (s: string): "warning" | "success" | "info" | "error" | "default" => {
  const upper = s.toUpperCase();
  switch (upper) {
    case "REQUESTED": return "warning";
    case "APPROVED": return "success";
    case "IN_TRANSIT": return "info";
    case "RECEIVED": return "info";
    case "RESOLVED": return "success";
    case "REJECTED": return "error";
    case "CANCELLED": return "default";
    default: return "default";
  }
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

type ReturnItemDetail = {
  product_name?: string;
  sku?: string;
  quantity?: number;
  reason_code?: string;
  decision?: string;
};

export default function PortalReturns() {
  const { portalSlug = "" } = useParams();
  const nav = useNavigate();
  const session = useMemo(() => readPortalSession(), []);

  const [returns, setReturns] = useState<PortalReturnSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<ReturnItemDetail[]>([]);
  const [expandLoading, setExpandLoading] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success" as "success" | "error", msg: "" });

  const isValid = session && session.portalSlug === portalSlug && session.verified;

  const fetchReturns = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listReturns();
      setReturns(data.returns || []);
    } catch (e) {
      const err = normalizeError(e);
      setToast({ open: true, severity: "error", msg: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isValid) fetchReturns();
  }, [isValid, fetchReturns]);

  if (!session || session.portalSlug !== portalSlug) return <Navigate to={`/r/${portalSlug}`} replace />;
  if (!session.verified) return <Navigate to={`/r/${portalSlug}/verify`} replace />;

  const toggleExpand = async (returnNumber: string) => {
    if (expanded === returnNumber) {
      setExpanded(null);
      return;
    }
    setExpanded(returnNumber);
    setExpandLoading(true);
    setExpandedItems([]);
    try {
      const resp: any = await getReturnDetails(returnNumber);
      const items = resp?.return?.items || resp?.items || [];
      setExpandedItems(items);
    } catch {
      setExpandedItems([]);
    } finally {
      setExpandLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelLoading(true);
    try {
      await cancelReturn(cancelTarget, cancelReason || undefined);
      setCancelTarget(null);
      setCancelReason("");
      setToast({ open: true, severity: "success", msg: "Return cancelled" });
      fetchReturns();
    } catch (e) {
      const err = normalizeError(e);
      setToast({ open: true, severity: "error", msg: err.message });
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <>
      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((p) => ({ ...p, open: false }))} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>

      <Stack spacing={2.5} sx={{ maxWidth: 560, width: "100%" }}>
        <Typography variant="h5">Your Returns</Typography>

        {returns.length === 0 ? (
          <Card>
            <CardContent sx={{ py: 5, textAlign: "center" }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>No returns found for this order</Typography>
              <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => nav(`/r/${portalSlug}`)}>
                Start a new return
              </Button>
            </CardContent>
          </Card>
        ) : (
          returns.map((ret) => {
            const isExpanded = expanded === ret.return_number;
            const canCancel = ret.status.toUpperCase() === "REQUESTED";

            return (
              <Card key={ret.return_number}>
                <CardContent sx={{ pb: isExpanded ? 0 : undefined }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    onClick={() => toggleExpand(ret.return_number)}
                    sx={{ cursor: "pointer" }}
                  >
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                        <Typography variant="body1" fontWeight={700}>{ret.return_number}</Typography>
                        <Chip
                          label={ret.status.replace(/_/g, " ")}
                          size="small"
                          color={statusColor(ret.status)}
                          variant="filled"
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Submitted {fmtDate(ret.requested_at)} &middot; {ret.total_items_count} item{ret.total_items_count !== 1 ? "s" : ""}
                      </Typography>
                    </Box>
                    {isExpanded ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />}
                  </Stack>
                </CardContent>

                <Collapse in={isExpanded}>
                  <Divider />
                  <CardContent>
                    {expandLoading ? (
                      <Stack alignItems="center" sx={{ py: 2 }}>
                        <CircularProgress size={24} />
                      </Stack>
                    ) : expandedItems.length > 0 ? (
                      <Stack spacing={1.5}>
                        {expandedItems.map((item, idx) => (
                          <Stack key={idx} direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {item.product_name || "Item"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.sku && `SKU ${item.sku}`}{item.quantity ? ` · Qty ${item.quantity}` : ""}
                                {item.reason_code ? ` · ${item.reason_code.replace(/_/g, " ")}` : ""}
                              </Typography>
                            </Box>
                            {item.decision && (
                              <Chip
                                label={item.decision}
                                size="small"
                                variant="outlined"
                                color={statusColor(item.decision)}
                              />
                            )}
                          </Stack>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No item details available</Typography>
                    )}

                    {canCancel && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        sx={{ mt: 2 }}
                        onClick={(e) => { e.stopPropagation(); setCancelTarget(ret.return_number); }}
                      >
                        Cancel this return
                      </Button>
                    )}
                  </CardContent>
                </Collapse>
              </Card>
            );
          })
        )}

        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => nav(`/r/${portalSlug}`)}>
          Back to portal
        </Button>
      </Stack>

      {/* Cancel confirmation */}
      <Dialog open={cancelTarget !== null} onClose={() => setCancelTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Return</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to cancel {cancelTarget}? This can't be undone.
          </Typography>
          <TextField
            label="Reason (optional)"
            fullWidth
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCancelTarget(null)} disabled={cancelLoading}>Keep it</Button>
          <Button variant="contained" color="error" onClick={handleCancel} disabled={cancelLoading}>
            {cancelLoading ? "Cancelling..." : "Yes, cancel"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
